import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";

import { ApiError } from "@/api/client";
import { AuthProvider } from "./context/auth-context";
import { AppRoutes } from "./routes";
import { ErrorBoundary } from "./components/patterns/ErrorBoundary";

/**
 * Query defaults for the whole application.
 *
 * The retry rule is the one worth reading. The default policy retries any
 * failure three times, which on a 403 or a 404 means three identical requests
 * and a three-second wait before the user is told what a single request already
 * knew. {@link ApiError.isRetryable} draws the line where it belongs: a 4xx
 * will fail again with the same body, a 5xx or a dropped connection might not.
 */
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) =>
                error instanceof ApiError ? error.isRetryable && failureCount < 2 : false,
            // Registry data does not change second to second, and a registrar
            // switching between two tabs should not watch both reload.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
        },
        mutations: {
            // Never. A retried POST is a second student, a second payment
            // intent, or a second status change in the history.
            retry: false,
        },
    },
});

/**
 * The application root.
 *
 * @returns The provider stack and the router
 */
export function App() {
    return (
        // Outermost, so it catches a throw from the providers themselves as
        // well as from any screen. A boundary inside the router would miss the
        // auth context, which is where the crash that motivated this one was.
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <AuthProvider>
                        <AppRoutes />
                        <Toaster position="top-right" richColors closeButton />
                    </AuthProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}
